import { FC } from "react";

import { Link } from "../../../../utils/routing";
import { pageUrl } from "../../../../utils/urls";

import { PublishStatus } from "./PublishStatus";
import { AllAssessmentDashboardDataType } from "./hooks";
import { getAssessmentStatusLabel } from "./utils";

import { AssessmentStatus } from "api/api-data-types";
import {
  ASSESSMENT_VIEW_QUERY_PARAM,
  SECONDARY_OVERLAY_QUERY_PARAM,
  SecondaryOverlay,
} from "components/constants";
import { MarkAssessmentCompleteMenu } from "components/materials/section/assessment/helpers";
import { ScrollableTableContainer } from "components/tables/ScrollableTableContainer";
import { TD, TH, THead, TR, Table } from "components/tables/TableElements";
import { relativeDayAndTimeFormat } from "i18n/helpers";
import { t } from "i18n/i18n";
import { Tooltip } from "mds/components/Tooltip";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { CircleInfoIcon, EyeNotVisibleIcon } from "mds/icons";
import { CSVData } from "utils/file";

interface StatusCellProps {
  row: AllAssessmentDashboardDataType["allAssessmentsTableRows"][number];
  sectionId: string;
}

const StatusCell: FC<StatusCellProps> = ({ row, sectionId }) => {
  const isNotAssessable = !row.status || row.status === AssessmentStatus.NOT_ASSESSABLE;

  if (isNotAssessable) {
    if (!row.dueAt) return <>-</>;
    return <>{relativeDayAndTimeFormat(new Date(row.dueAt))}</>;
  }

  const isComplete = row.isAssessmentComplete || row.isMarkedComplete;

  return (
    <SquareButton
      as={Link}
      eventName={`Course - All Assessments / ${isComplete ? "View assessed page" : "Start assessing"}`}
      eventProps={{
        pageTitle: row.title,
        pageUrl: pageUrl(sectionId, row.id),
        pageId: row.id,
      }}
      kind={isComplete ? "complete" : "accent"}
      size="xs"
      to={
        isComplete
          ? pageUrl(sectionId, row.id, {
              additionalParams: `${SECONDARY_OVERLAY_QUERY_PARAM}=${SecondaryOverlay.AssessmentSummary}`,
            })
          : pageUrl(sectionId, row.id, {
              additionalParams: `${ASSESSMENT_VIEW_QUERY_PARAM}=1`,
            })
      }
    >
      {getAssessmentStatusLabel(
        row.isMarkedComplete,
        row.assessedSubmissionsCount,
        row.isAssessmentComplete,
      )}
      {row.unassessedLateCount > 0 && (
        <Tooltip
          text={t("assessment.late_assessment_count", {
            count: row.unassessedLateCount,
          })}
        >
          <CircleInfoIcon />
        </Tooltip>
      )}
    </SquareButton>
  );
};

type AssessmentStatusTableProps = {
  tableRows: AllAssessmentDashboardDataType["allAssessmentsTableRows"];
  sectionId: string;
  title: string;
  hideMarkCompleteMenu?: boolean;
  emptyDescription: string;
  exportData?: CSVData | (() => Promise<CSVData>);
  onRefetch?: () => void;
};

export const AssessmentStatusTable: FC<AssessmentStatusTableProps> = ({
  tableRows,
  exportData,
  sectionId,
  title,
  hideMarkCompleteMenu,
  emptyDescription,
  onRefetch,
}) => {
  if (tableRows.length === 0) {
    return (
      <ScrollableTableContainer title={title}>
        <div className="ml-4 text-black-tint-40">{emptyDescription}</div>
      </ScrollableTableContainer>
    );
  }

  return (
    <ScrollableTableContainer exportData={exportData} title={title}>
      <Table>
        {/* Headers: Topic / Page / Status / Publishing / Assessed / Page Type /Avg. Points / Outcomes / Last Submitted */}
        <THead>
          <TR className="border-black-tint-70">
            <TH className="w-6/12 pl-4">{t("assessment_dashboard.all.page_topic_header_title")}</TH>

            <TH className="w-6/12">{t("assessment_dashboard.all.page_header_title")}</TH>

            <TH className="w-16">{t("assessment_dashboard.all.status_header_title")}</TH>

            <TH className="w-16">
              <Tooltip text={t("assessment_dashboard.all.publishing_header_tooltip")}>
                {t("assessment_dashboard.all.publishing_header_title")}
              </Tooltip>
            </TH>

            <TH className="w-16">
              <Tooltip text={t("assessment_dashboard.all.assessed_header_tooltip")}>
                {t("assessment_dashboard.all.assessed_header_title")}
              </Tooltip>
            </TH>

            <TH className="w-16">
              <Tooltip text={t("assessment_dashboard.all.assessment_category_header_tooltip")}>
                {t("assessment_dashboard.all.assessment_category_header_title")}
              </Tooltip>
            </TH>

            <TH className="w-16">
              <Tooltip text={t("assessment_dashboard.all.avg_points_header_tooltip")}>
                {t("assessment_dashboard.all.avg_points_header_title")}
              </Tooltip>
            </TH>

            <TH className="w-16">
              <Tooltip text={t("assessment_dashboard.all.outcomes_header_tooltip")}>
                {t("assessment_dashboard.all.outcomes_header_title")}
              </Tooltip>
            </TH>

            <TH className="w-16">
              <Tooltip text={t("assessment_dashboard.all.last_submitted_header_tooltip")}>
                {t("assessment_dashboard.all.last_submitted_header_title")}
              </Tooltip>
            </TH>
          </TR>
        </THead>

        <tbody>
          {tableRows.map((row) => (
            <TR key={row.id}>
              <TD className="max-w-52 truncate py-2.5 pl-[17px]" title={row.topicTitle || "–"}>
                {row.topicTitle || "–"}
              </TD>

              <TD className="max-w-40 truncate" title={row.title}>
                <Link
                  className="body-xs flex min-w-0 items-center gap-1 truncate text-blue-shade-20 underline"
                  to={pageUrl(sectionId, row.id)}
                >
                  {row.hidden && (
                    <Tooltip text={t("assessment_dashboard.all.hidden_page_tooltip")}>
                      <EyeNotVisibleIcon className="shrink-0 text-black-tint-40" />
                    </Tooltip>
                  )}
                  {row.title}
                </Link>
              </TD>

              <TD className="max-w-40 truncate">
                <StatusCell row={row} sectionId={sectionId} />
              </TD>

              <TD>
                <PublishStatus status={row.publishStatus} />
              </TD>

              <TD>
                {row.publishStatus === "na"
                  ? "-"
                  : t("assessment.assessed_count.number", {
                      count: row.assessedSubmissionsCount,
                      total: row.totalSubmissionsCount,
                    })}
              </TD>

              <TD>{row.assessmentCategory}</TD>

              <TD>{row.avgPoints}</TD>

              <TD>
                <Tooltip text={row.outcomesTooltip}>{row.outcomesCount.toString()}</Tooltip>
              </TD>

              <TD>{row.lastSubmittedDate}</TD>

              {!hideMarkCompleteMenu && (
                <td>
                  <MarkAssessmentCompleteMenu
                    isMarkedComplete={row.isMarkedComplete}
                    pageId={row.id}
                    pageTitle={row.title}
                    sectionId={sectionId}
                    isCourseAssessment
                    onChange={onRefetch ?? (() => {})}
                  />
                </td>
              )}
            </TR>
          ))}
        </tbody>
      </Table>
    </ScrollableTableContainer>
  );
};
